import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Products', path: '/products' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Blog', path: '/blog' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-md border-b-2 border-gray-200'
            : 'bg-transparent'
        }`}
      >
        <nav className="mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img 
                src="https://static.readdy.ai/image/8f4b99b6f50522f57c0c4089909ec764/71b4496442f888e58b7226680dc03ff3.png" 
                alt="Carver Dental Laboratory" 
                className="h-28 w-auto group-hover:scale-105 transition-transform duration-300"
                style={{ objectFit: 'contain' }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-4 py-2 text-sm font-medium transition-colors duration-300 group whitespace-nowrap ${
                    location.pathname === item.path
                      ? isScrolled
                        ? 'text-gold-600'
                        : 'text-white'
                      : isScrolled
                      ? 'text-darkgray-500 hover:text-gold-600'
                      : 'text-white/90 hover:text-white'
                  }`}
                >
                  {item.name}
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 bg-gold-500 transform origin-left transition-transform duration-300 ${
                      location.pathname === item.path ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                    }`}
                  ></span>
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <Link
                to="/quote"
                className={`px-5 py-2.5 text-sm font-semibold rounded-lg border-2 transition-all duration-300 whitespace-nowrap ${
                  isScrolled
                    ? 'border-gold-500 text-gold-600 hover:bg-gold-500 hover:text-white'
                    : 'border-white text-white hover:bg-white hover:text-gold-600'
                }`}
              >
                Request Quote
              </Link>
              <Link
                to="/contact"
                className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-gold-500 to-gold-700 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 whitespace-nowrap"
              >
                Contact Us
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-300 ${
                isScrolled ? 'text-darkgray-500 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden bg-white"
            style={{ top: '96px' }}
          >
            <div className="flex flex-col h-full overflow-y-auto">
              <div className="flex-1 px-6 py-8 space-y-2">
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={item.path}
                      className={`block px-4 py-3 text-lg font-medium rounded-lg transition-colors duration-300 ${
                        location.pathname === item.path
                          ? 'bg-gray-100 text-gold-600'
                          : 'text-darkgray-500 hover:bg-gray-50'
                      }`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
              </div>
              <div className="p-6 space-y-3 border-t border-gray-200">
                <Link
                  to="/quote"
                  className="block w-full px-5 py-3 text-center text-sm font-semibold text-gold-600 border-2 border-gold-500 rounded-lg hover:bg-gold-500 hover:text-white transition-all duration-300 whitespace-nowrap"
                >
                  Request Quote
                </Link>
                <Link
                  to="/contact"
                  className="block w-full px-5 py-3 text-center text-sm font-semibold text-white bg-gradient-to-r from-gold-500 to-gold-700 rounded-lg hover:shadow-lg transition-all duration-300 whitespace-nowrap"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
